import React, { useEffect, useRef, useState } from 'react';

import { IContentfulImage } from '../../../interfaces/contentful-image.interface';
import LoginRegister from '../../user-profile/login-register';
import { DynamicOfferRedemption } from './dynamic-offer-redemption';
import { claimAntavoReward } from './offer-redemption-api-helper';
import { OfferRedemptionErrorScreen } from './offer-redemption-error-screen';
import { StaticOfferRedemption } from './static-offer-redemption';
import { decryptQuery } from '@/helpers/encode-decode';
import { useUser } from '@/providers/UserProvider';
import {
  useGetLibraryConstantsDictionaryData,
  useTenantConfig,
} from '@/providers/ConfigProvider';

interface LivliFamilyOfferRedemptionProps {
  offerRedemptionDetails?: {
    internalName: string;
    richText: any;
  };
  offerRedemptionMechanism?: string;
  handleClose: () => void;
  isQRCodeScanned: boolean;
  scannedText?: string;
  contentful_id?: string;
  tenantDetails: {
    offerName?: string;
    tenantName?: string;
    tenantLogo?: IContentfulImage;
  };
  node_locale: string;
  meetingPlaceHomePage?: any;
  beResourceId?: string;
  offerTenantId?: string | string[];
}

// Antavo nests the error code at multiple levels (errorMessage.code / message.code)
const extractErrorCode = (source: any): number | string | null =>
  source?.code ??
  source?.errorMessage?.code ??
  source?.message?.code ??
  source?.error?.code ??
  null;

export const LivliFamilyOfferRedemption = ({
  offerRedemptionDetails,
  offerRedemptionMechanism,
  handleClose,
  isQRCodeScanned,
  scannedText,
  contentful_id,
  tenantDetails,
  node_locale,
  meetingPlaceHomePage,
  beResourceId,
  offerTenantId,
}: LivliFamilyOfferRedemptionProps) => {
  const [claimSuccessful, setClaimSuccessful] = useState(false);
  const [claimFailed, setClaimFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const { user } = useUser();
  const dictionary = useGetLibraryConstantsDictionaryData();
  const config = useTenantConfig();

  const dictionaryRef = useRef(dictionary);

  useEffect(() => {
    dictionaryRef.current = dictionary;
  }, [dictionary]);

  // Localised message keyed by the API error code; generic fallback when absent
  const getErrorMessageByCode = (code?: number | string | null): string => {
    const dict = dictionaryRef.current;
    const generic =
      dict?.dynamicOfferErrorMessageText ?? 'Failed to claim the offer.';
    if (code === undefined || code === null || code === '') return generic;
    return dict?.[String(code)] ?? generic;
  };

  useEffect(() => {
    if (offerRedemptionMechanism !== 'Dynamic') {
      setClaimFailed(true);
      setErrorMessage(
        dictionaryRef.current?.dynamicOfferErrorMessageText ?? '',
      );
      setLoading(false);
      return;
    }

    if (scannedText === 'CAMERA_PERMISSION_DENIED') {
      setClaimFailed(true);
      setErrorMessage(
        dictionaryRef.current?.cameraPermisssionDenied ??
        'Camera permission denied. Please enable camera access.',
      );
      setLoading(false);
      return;
    }

    if (scannedText === 'CAMERA_ERROR') {
      setClaimFailed(true);
      setErrorMessage(
        dictionaryRef.current?.cameraPermisssionDenied ??
        'Unable to access the camera. Please try again.',
      );
      setLoading(false);
      return;
    }

    if (!scannedText) {
      setLoading(false);
      return;
    }

    // Decrypt the scanned QR code
    const scannedTenantId = decryptQuery(scannedText);
    if (!scannedTenantId || scannedTenantId?.length === 0) {
      setClaimFailed(true);
      setErrorMessage(dictionaryRef.current?.wrongQrCode ?? 'Invalid QR Code');
      setLoading(false);
      return;
    }

    // shopId is a list field in Contentful — the scanned id must be one of them
    const allowedTenantIds = Array.isArray(offerTenantId)
      ? offerTenantId
      : offerTenantId
        ? [offerTenantId]
        : [];
    if (!allowedTenantIds.includes(scannedTenantId)) {
      setClaimFailed(true);
      setErrorMessage(dictionaryRef.current?.wrongQrCode ?? 'Invalid QR Code');
      setLoading(false);
      return;
    }

    // Offer not configured for claiming (missing resource) or missing user id
    if (!beResourceId || !user?.id) {
      setClaimFailed(true);
      setErrorMessage(
        dictionaryRef.current?.dynamicOfferErrorMessageText ?? '',
      );
      setLoading(false);
      return;
    }

    const claim = async () => {
      try {
        setLoading(true);
        setClaimFailed(false);
        setClaimSuccessful(false);
        const response: any = await claimAntavoReward(
          { resourceId: beResourceId },
          { 'X-Current-Language': node_locale },
        );
        // Backend can return 200 with an explicit failure signal
        if (response?.success === false || response?.error) {
          const code = extractErrorCode(response);
          setClaimSuccessful(false);
          setClaimFailed(true);
          setErrorMessage(getErrorMessageByCode(code));
          return;
        }
        setClaimSuccessful(true);
        setClaimFailed(false);
      } catch (err: any) {
        const code = extractErrorCode(err?.raw) ?? extractErrorCode(err);
        setClaimSuccessful(false);
        setClaimFailed(true);
        setErrorMessage(getErrorMessageByCode(code));
        console.error('Failed to claim reward', err);
      } finally {
        setLoading(false);
      }
    };

    claim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedText, offerRedemptionMechanism]);

  if (!user) {
    return (
      <LoginRegister
        node_locale={node_locale}
        brandId={meetingPlaceHomePage?.businessUnitCode ?? ''}
        config={config}
        handleClose={handleClose}
      />
    );
  }

  if (!offerRedemptionMechanism) {
    return (
      <OfferRedemptionErrorScreen
        contentful_id={contentful_id}
        handleClose={handleClose}
        tenantDetails={tenantDetails}
        errorMessage={errorMessage}
        node_locale={node_locale}
      />
    );
  }

  if (offerRedemptionMechanism === 'Static') {
    return (
      <StaticOfferRedemption
        contentful_id={contentful_id}
        offerRedemptionDetails={offerRedemptionDetails}
        tenantDetails={tenantDetails}
        handleClose={handleClose}
        node_locale={node_locale}
      />
    );
  }

  if (offerRedemptionMechanism === 'Dynamic') {
    if (loading) {
      return (
        <div
          role="status"
          className="absolute md:top-[45%] left-[45%] top-[100%]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 102 102"
            className="h-12 w-12 animate-spin fill-brand-yellow-1"
          >
            <path
              d="M96.57 39.43c6.31 25.75-9.46 51.74-35.21 58.05S9.63 88.02 3.32 62.27c-.91-3.72-1.37-7.53-1.38-11.36a47.85 47.85 0 0 1 13.57-33.44c4.63-4.75 12.14.83 7.32 6.46-6.46 7.21-11.59 15.87-11.59 26.31 0 22.48 18.22 40.7 40.7 40.7s40.7-18.22 40.7-40.7c0-3.23-.39-6.46-1.15-9.6-.8-3.66 4.1-4.89 5.08-1.21Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
      );
    }
    if (isQRCodeScanned && claimSuccessful) {
      return (
        <DynamicOfferRedemption
          offerRedemptionDetails={offerRedemptionDetails}
          tenantDetails={tenantDetails}
          contentful_id={contentful_id}
          handleClose={handleClose}
          node_locale={node_locale}
        />
      );
    }
    if (isQRCodeScanned && claimFailed) {
      return (
        <OfferRedemptionErrorScreen
          contentful_id={contentful_id}
          handleClose={handleClose}
          tenantDetails={tenantDetails}
          errorMessage={errorMessage}
          node_locale={node_locale}
        />
      );
    }
    return null;
  }

  return null;
};

